import React, { useState } from 'react';
import { Layout, Dropdown, Avatar, Space, Badge, theme, Typography } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProLayout, type MenuDataItem } from '@ant-design/pro-components';
import { useUserStore } from '@/stores/user';
import { storage } from '@/utils/storage';
import { menuConfig, iconMap } from '@/routes/config';
import type { MenuItem } from '@/types';
import styles from './basicLayout.module.css';

const { Text } = Typography;
const { Content } = Layout;

const convertMenu = (items: MenuItem[], permissions: string[]): MenuItem[] => {
  return items
    .filter((item) => !item.permission || permissions.includes(item.permission))
    .map((item) => ({
      ...item,
      icon:
        typeof item.icon === 'string' && iconMap[item.icon]
          ? React.createElement(iconMap[item.icon])
          : item.icon,
      children: item.children ? convertMenu(item.children, permissions) : undefined
    }));
};

const Logo = () => (
  <div className={styles.logo}>
    <div className={styles.logoIcon}>
      <span>S</span>
    </div>
    <span className={styles.logoText}>SWAG Dance</span>
  </div>
);

export default function BasicLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions, clearUser } = useUserStore();
  const [collapsed, setCollapsed] = useState(false);
  const { token } = theme.useToken();

  const filteredMenu = convertMenu(menuConfig, permissions);

  const handleLogout = () => {
    storage.clear();
    clearUser();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: '个人设置',
      onClick: () => navigate('/system/admin-users')
    },
    {
      type: 'divider' as const
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  return (
    <ProLayout
      title="SWAG Dance"
      logo={<Logo />}
      layout="mix"
      splitMenus={false}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      location={{ pathname: location.pathname }}
      menuDataRender={() => filteredMenu as MenuDataItem[]}
      menuItemRender={(item: MenuDataItem, dom: React.ReactNode) => (
        <div
          className={styles.menuItem}
          onClick={() => navigate(item.path || '/')}
          style={{ cursor: 'pointer' }}
        >
          {dom}
        </div>
      )}
      headerContentRender={() => (
        <div
          className={styles.collapseBtn}
          onClick={() => setCollapsed(!collapsed)}
          style={{ color: token.colorTextSecondary }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      )}
      rightContentRender={() => (
        <Space size={20} className={styles.rightContent}>
          <Badge count={0} size="small">
            <div className={styles.iconBtn} style={{ color: token.colorTextSecondary }}>
              <BellOutlined />
            </div>
          </Badge>
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
          >
            <Space className={styles.userInfo} size={10}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                src={user?.avatar}
                style={{ backgroundColor: token.colorPrimary }}
              />
              <div className={styles.userMeta}>
                <Text className={styles.userName} ellipsis>
                  {user?.realName || user?.username}
                </Text>
                <Text type="secondary" className={styles.userRole} ellipsis>
                  {user?.roles?.map((r) => r.name).join(', ') || '管理员'}
                </Text>
              </div>
            </Space>
          </Dropdown>
        </Space>
      )}
      breadcrumbRender={(routers = []) => {
        return [
          {
            path: '/dashboard',
            breadcrumbName: '首页'
          },
          ...routers
        ];
      }}
      footerRender={() => (
        <div className={styles.footer}>
          <Text type="secondary">SWAG Dance 管理后台 ©{new Date().getFullYear()}</Text>
        </div>
      )}
    >
      <Content className={styles.content}>{children}</Content>
    </ProLayout>
  );
}
